/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */
import React, { useContext, useState } from 'react'
import { Roles } from 'utils/enums'
import { Tab } from '@mui/material'
import { translate } from 'utils/propKeysTranslations'
import Table from 'components/common/Table'
import TableButton from 'components/common/Buttons/TableButton'
import useQuery from 'hooks/useQuery'
import Calls from 'components/admin/Tables/Calls'
import PasswordPrompt from 'components/Prompts/PasswordPrompt'
import { useNavigate, useParams } from 'react-router-dom'
import RoleTabs from 'components/admin/Tabs'
import { AdminDataContext } from 'context/AdminDataContext'

const DELETED_QUERY = { deleted: true }

const DeletedUsers = () => {
  const navigate = useNavigate()
  const { makeRequest: restore } = useQuery({
    isControlled: true,
    defaultMethod: 'post',
    toastOptions: {
      showFeedbackMessage: true,
      resultMessageSuccess: 'Usuario restaurado',
      resultMessageError: { 403: 'Máximo número de cuentas alcanzado' },
    },
  })

  const { makeRequest: deleteUser } = useQuery({
    isControlled: true,
    defaultMethod: 'delete',
    toastOptions: {
      showFeedbackMessage: true,
      resultMessageSuccess: 'Usuario eliminado permanentemente',
      resultMessageError: { 401: 'Contraseña incorrecta' },
    },
    reloadTableOnResolve: true,
    disableAutoLogoutOn401: true,
  })

  const params = useParams()

  const {
    fetchAccounts, fetchClients, fetchMediators, fetchTranslators,
  } = useContext(AdminDataContext)

  const [roleObject, setRoleObject] = useState(
    rolesToShow.find(({ mainPath }) => params.role === mainPath)
      || rolesToShow[0],
  )
  const [deletePrompt, setDeletePrompt] = useState(null)

  const getColumnForRestoring = (rolePath) => ({
    Header: 'Restaurar usuario',
    accessor: ({ id }) => (
      <TableButton
        clickHandler={async () => {
          await restore({ endpoint: `/admins/${rolePath}/${id}` })
          if (rolePath === Roles.Mediator.mainPath) {
            await Promise.all([
              fetchMediators(),
              fetchTranslators(),
              fetchClients(),
              fetchAccounts(),
            ])
          } else if (rolePath === Roles.Client.mainPath) {
            await Promise.all([fetchClients(), fetchAccounts()])
          } else if (rolePath === Roles.Translator.mainPath) {
            await fetchTranslators()
          } else if (rolePath === Roles.Account.mainPath) {
            await fetchAccounts()
          }
          navigate(`/admins/management/${rolePath}`)
        }}
      >
        Restaurar
      </TableButton>
    ),
  })

  const getColumnForDeletingPermanently = (rolePath) => ({
    Header: 'Borrar Permanentemente',
    accessor: ({ id }) => (
      <TableButton
        clickHandler={() => {
          const title = 'Está seguro que quiere continuar?'
          let message = 'Esta acción hará que se borren completamente todos los registros del usuario'
          if (rolePath === Roles.Client.mainPath) { message += ' y cuentas asociadas' }
          setDeletePrompt({
            message,
            title,
            onOk: async (authPassword) => {
              await deleteUser({
                endpoint: `/admins/${rolePath}/${id}?force=true`,
                body: { authPassword },
              })
            },
          })
        }}
      >
        Borrar
      </TableButton>
    ),
  })
  let SelectedSection
  if (roleObject.role === Roles.Translator.role) {
    SelectedSection = (
      <TranslatorsSection
        getColumnForRestoring={getColumnForRestoring}
        getColumnForDeletingPermanently={getColumnForDeletingPermanently}
      />
    )
  }
  if (roleObject.role === Roles.Client.role) {
    SelectedSection = (
      <ClientsSection
        getColumnForRestoring={getColumnForRestoring}
        getColumnForDeletingPermanently={getColumnForDeletingPermanently}
      />
    )
  }
  if (roleObject.role === Roles.Mediator.role) {
    SelectedSection = (
      <MediatorSection
        getColumnForRestoring={getColumnForRestoring}
        getColumnForDeletingPermanently={getColumnForDeletingPermanently}
      />
    )
  }
  if (roleObject.role === Roles.Account.role) {
    SelectedSection = (
      <AccountsSection
        getColumnForRestoring={getColumnForRestoring}
        getColumnForDeletingPermanently={getColumnForDeletingPermanently}
      />
    )
  }
  return (
    <div className='flex flex-col'>
      <RoleTabs options={rolesToShow} tab={roleObject} setTab={setRoleObject}>
        {SelectedSection}
      </RoleTabs>
      {deletePrompt && (
        <PasswordPrompt prompt={deletePrompt} setShowPrompt={setDeletePrompt} />
      )}
    </div>
  )
}

const rolesToShow = [
  Roles.Translator,
  Roles.Client,
  Roles.Mediator,
  Roles.Account,
]

const TRANSLATOR_CALLS_COLUMNS_TO_SKIP = ['translatorEmail']
const TranslatorsSection = ({
  getColumnForRestoring,
  getColumnForDeletingPermanently,
}) => {
  const [selectedTranslator, setSelectedTranslator] = useState(null)

  const TRANSLATOR_ADITIONAL_COLUMNS = [
    {
      Header: 'Llamadas',
      // accessor only gets called when mounting.
      Cell: ({
        row: {
          original: { id, email, calls },
        },
        cellCustomData,
      }) => {
        return (
          <TableButton
            selected={id === cellCustomData.selectedTranslator?.id}
            clickHandler={() => {
              setSelectedTranslator((ps) =>
                ps?.id === id
                  ? null
                  : {
                    title: email,
                    endpoint: calls,
                    id,
                  })
            }}
          >
            Ver Llamadas
          </TableButton>
        )
      },
    },
    getColumnForRestoring(Roles.Translator.mainPath),
    getColumnForDeletingPermanently(Roles.Translator.mainPath),
  ]
  return (
    <>
      <Table
        endpoint='/admins/translators'
        disableAutoScroll
        urlQueries={DELETED_QUERY}
        aditionalColumns={TRANSLATOR_ADITIONAL_COLUMNS}
        showDeleted
      />
      {selectedTranslator && (
        <Calls
          endpoint={selectedTranslator.endpoint}
          title={`Llamadas de ${selectedTranslator.title}`}
          columnsToSkip={TRANSLATOR_CALLS_COLUMNS_TO_SKIP}
        />
      )}
    </>
  )
}

const COLUMNS_TO_REPLACE = {
  accountsCount: {
    id: 'accountsCount',
    Header: 'Cuentas en uso',
    accessor: ({ accountsCount, maxAccounts }) =>
      `${accountsCount} de ${maxAccounts}`,
  },
}
const COLUMNS_TO_SKIP = ['maxAccounts']
const ClientsSection = ({
  getColumnForRestoring,
  getColumnForDeletingPermanently,
}) => {
  const CLIENT_ADITIONAL_COLUMNS = [
    getColumnForRestoring(Roles.Client.mainPath),
    getColumnForDeletingPermanently(Roles.Client.mainPath),
  ]

  return (
    <Table
      endpoint='/admins/clients'
      disableAutoScroll
      columnsToReplace={COLUMNS_TO_REPLACE}
      urlQueries={DELETED_QUERY}
      columnsToSkip={COLUMNS_TO_SKIP}
      aditionalColumns={CLIENT_ADITIONAL_COLUMNS}
      showDeleted
    />
  )
}

const MediatorSection = ({
  getColumnForRestoring,
  getColumnForDeletingPermanently,
}) => {
  const MEDIATOR_ADITIONAL_COLUMNS = [
    getColumnForRestoring(Roles.Mediator.mainPath),
    getColumnForDeletingPermanently(Roles.Mediator.mainPath),
  ]
  return (
    <Table
      endpoint='/admins/mediators'
      disableAutoScroll
      urlQueries={DELETED_QUERY}
      aditionalColumns={MEDIATOR_ADITIONAL_COLUMNS}
      showDeleted
    />
  )
}

const ACCOUNT_CALLS_COLUMNS_TO_SKIP = ['accountFullName', 'accountEmail']
const AccountsSection = ({
  getColumnForRestoring,
  getColumnForDeletingPermanently,
}) => {
  const [selectedAccount, setSelectedAccount] = useState(null)

  const ACCOUNT_ADITIONAL_COLUMNS = [
    {
      Header: 'Llamadas',
      Cell: ({
        row: {
          original: {
            id, firstName, lastName, calls,
          },
        },
        cellCustomData,
      }) => (
        <TableButton
          selected={id === cellCustomData.selectedAccount?.id}
          clickHandler={() =>
            setSelectedAccount((ps) =>
              ps?.id === id
                ? null
                : {
                  title: `${firstName} ${lastName}`,
                  endpoint: calls,
                  id,
                })}
        >
          Ver Llamadas
        </TableButton>
      ),
    },
    getColumnForRestoring(Roles.Account.mainPath),
    getColumnForDeletingPermanently(Roles.Account.mainPath),
  ]
  return (
    <>
      <Table
        endpoint='/admins/accounts'
        disableAutoScroll
        urlQueries={DELETED_QUERY}
        aditionalColumns={ACCOUNT_ADITIONAL_COLUMNS}
        showDeleted
      />
      {selectedAccount && (
        <Calls
          endpoint={selectedAccount.endpoint}
          title={`Llamadas de ${selectedAccount.title}`}
          columnsToSkip={ACCOUNT_CALLS_COLUMNS_TO_SKIP}
        />
      )}
    </>
  )
}

export default DeletedUsers
